import { ApiError, call, sessionIsEnding } from './api.ts';

export type IntakeActor = { role: 'operator'; operator_id: string; upload_device_id: string; upload_centre_id: string };
export type IntakeDeclaration = { handoverId: string; sessionId: string; batchId: string; operatorId: string; centreId: string; deviceId: string; phase: 'committing' | 'committed' | 'refused' };
export type IntakeFile = { relative_path: string; bytes: number; sha256: string };
export type IntakeJob = {
  id: string; basename: string; phase: string; reason: string | null; handover_id: string; session_id: string;
  batch_id: string; operator_id: string; acting_operator_id?: string; centre_id: string; device_id: string; bytes: number; progress_bytes: number;
  ingest_id?: string; target_batch_id?: string; record?: { episode_id: string; [key: string]: unknown }; files?: IntakeFile[];
};
export type IntakeRegistration = { episode_id: string; ingest_id: string; batch_id: string;
  resolution_state: 'resolved' | 'quarantined'; collection_session_id: string | null };
export type IntakeStatus = IntakeRegistration & { state: 'not_requested' | 'queued' | 'verifying' | 'retry_scheduled' | 'failed' | 'verified';
  reason: string | null; files_verified: number; files_total: number; retry_at: string | null; can_take_over: boolean;
  current_operator_owns_job: boolean;
  originality?: { assessment_id: string; status: 'complete' | 'unavailable' | 'failed'; reason: string | null; current: boolean } | null };
export type HelperInfo = { version: number; centre_id: string; device_id: string; recovery_required: boolean;
  max_session_bytes: number; max_single_file_bytes: number; max_files: number; reserve_bytes: number };
export type CardFolder = { id: string; basename: string; files?: number; bytes?: number; reason: string | null };
const UUID = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/;
const RECEIPT = 'playerone.counter.committed.v1';
const receiptKey = (operatorId: string, centreId: string, deviceId: string) => `${RECEIPT}.${operatorId}.${centreId}.${deviceId}`;
const requestSignal = (signal?: AbortSignal) => {
  const deadline = AbortSignal.timeout(20000);
  return signal ? AbortSignal.any([signal, deadline]) : deadline;
};

function declarations(storage: Storage, key: string): IntakeDeclaration[] {
  const raw = storage.getItem(key);
  if (raw === null) return [];
  if (raw.length > 100000) throw new Error('declaration_recovery_required');
  let value: unknown;
  try { value = JSON.parse(raw); } catch { throw new Error('declaration_recovery_required'); }
  if (!Array.isArray(value) || value.length > 200 || value.some(row => !row ||
    Object.keys(row).sort().join(',') !== 'batchId,centreId,deviceId,handoverId,operatorId,phase,sessionId' ||
    !['committing','committed','refused'].includes(row.phase) ||
    Object.entries(row).some(([key,id]) => key !== 'phase' && (typeof id !== 'string' || !UUID.test(id))))) throw new Error('declaration_recovery_required');
  return value;
}
export function pendingDeclarations(actor: IntakeActor, storage = localStorage) {
  return declarations(storage, receiptKey(actor.operator_id, actor.upload_centre_id, actor.upload_device_id))
    .filter(d => d.operatorId === actor.operator_id && d.centreId === actor.upload_centre_id && d.deviceId === actor.upload_device_id);
}
export function saveDeclaration(row: IntakeDeclaration, storage = localStorage) {
  if (!['committing','committed','refused'].includes(row.phase) || Object.entries(row).some(([key,id]) => key !== 'phase' && !UUID.test(id))) throw new Error('invalid_declaration_identity');
  const key = receiptKey(row.operatorId, row.centreId, row.deviceId);
  const rows = declarations(storage, key), old = rows.find(r => r.sessionId === row.sessionId);
  if (old && Object.entries(row).some(([key,value]) => key !== 'phase' && old[key as keyof IntakeDeclaration] !== value)) throw new Error('declaration_identity_changed');
  if (old && old.phase !== 'committed') old.phase = row.phase;
  if (!old) rows.push(row);
  if (rows.length > 200) throw new Error('declaration_queue_full');
  storage.setItem(key, JSON.stringify(rows));
}
export function acknowledgedDeclaration(row: IntakeDeclaration, storage = localStorage) {
  const key = receiptKey(row.operatorId, row.centreId, row.deviceId);
  storage.setItem(key, JSON.stringify(declarations(storage, key).filter(r => r.sessionId !== row.sessionId)));
}
export function retireDeclaration(row: IntakeDeclaration, reason: string, storage = localStorage) {
  const key = receiptKey(row.operatorId, row.centreId, row.deviceId), rows = declarations(storage, key);
  const current = rows.find(r => r.sessionId === row.sessionId);
  if (!current || current.phase === 'committing' || !reason.trim() || reason.length > 500) throw new Error('declaration_retirement_refused');
  const raw = storage.getItem(key + '.retired');
  if (raw && raw.length > 200000) throw new Error('declaration_recovery_required');
  const history = raw ? JSON.parse(raw) : [];
  if (!Array.isArray(history) || history.length > 200) throw new Error('declaration_recovery_required');
  // ponytail: last 200 local reminder removals only. Server declaration/audit retention is unchanged.
  storage.setItem(key + '.retired', JSON.stringify([...history.filter(r => r.sessionId !== row.sessionId),
    { ...current, reason: reason.trim(), retiredAt: new Date().toISOString() }].slice(-200)));
  acknowledgedDeclaration(current, storage); // History must persist before the pending reminder is removed.
}
export async function intakeActor(signal?: AbortSignal): Promise<IntakeActor> {
  const actor = await call<IntakeActor>('/whoami', { signal: requestSignal(signal) });
  if (actor?.role !== 'operator' || ![actor.operator_id, actor.upload_centre_id, actor.upload_device_id].every(id => UUID.test(id))) {
    throw new Error('counter_identity_required');
  }
  return actor;
}
export async function confirmDeclaration(row: IntakeDeclaration) {
  if (![row.handoverId, row.sessionId].every(id => UUID.test(id))) throw new Error('invalid_declaration_identity');
  const actor = await intakeActor();
  if (actor.operator_id !== row.operatorId || actor.upload_centre_id !== row.centreId ||
    actor.upload_device_id !== row.deviceId) throw new Error('operator_changed');
  const result = await call<{ handover_id: string; session_id: string; state: string }>(
    `/handovers/${row.handoverId}/sessions/${row.sessionId}/confirmation`, { signal: requestSignal() });
  if (!result || result.handover_id !== row.handoverId || result.session_id !== row.sessionId ||
    result.state !== 'committed') throw new Error('declaration_unconfirmed');
  saveDeclaration({ ...row, phase: 'committed' });
  return actor;
}
export function ownsJob(actor: IntakeActor, job: IntakeJob) {
  return (job.acting_operator_id ?? job.operator_id) === actor.operator_id && job.centre_id === actor.upload_centre_id && job.device_id === actor.upload_device_id;
}
export function operatorView({ job, actor, cloud }: { job: IntakeJob; actor: IntakeActor | null;
  cloud: { status: IntakeStatus | null; fetch: 'idle' | 'ok' | 'error' } }) {
  type Action = 'continue' | 'pause' | 'verify' | 'retry' | 'takeover' | 'reconnect';
  const view: { kind: 'attention' | 'ready' | 'copying' | 'uploading' | 'paused' | 'checking' | 'done';
    progress: { value?: number; max?: number } | null; primary: Action | null; secondary: Action | null;
    problem: string | null; note: string | null } = {
    kind: 'attention', progress: null, primary: null, secondary: null, problem: null, note: null,
  };
  const { status, fetch } = cloud;
  if (job.phase === 'abandoned') return { ...view, problem: 'abandoned' };
  if (!actor || !ownsJob(actor, job)) return { ...view, problem: 'different',
    primary: status?.current_operator_owns_job ? 'reconnect' as const : status?.can_take_over ? 'takeover' as const : null };
  if (job.phase === 'queued') return { ...view, kind: 'ready' as const, primary: 'continue' as const, progress: {} };
  if (['copying','measuring'].includes(job.phase)) return { ...view, kind: 'copying' as const, primary: 'pause' as const,
    progress: job.phase === 'copying' && job.bytes > 0 ? { value: job.progress_bytes, max: job.bytes } : {} };
  if (job.phase === 'uploading') return { ...view, kind: 'uploading' as const, primary: 'pause' as const, progress: {}, note: 'missingParts' };
  if (job.phase === 'awaiting_plan') return { ...view, kind: 'ready' as const, primary: 'continue' as const };
  if (['paused','failed'].includes(job.phase) && !job.ingest_id) return { ...view,
    kind: job.phase === 'paused' ? 'paused' as const : 'attention' as const, primary: 'continue' as const, problem: job.reason ?? 'error' };
  if (job.ingest_id && fetch !== 'ok') return { ...view, kind: fetch === 'idle' ? 'checking' as const : 'attention' as const,
    problem: fetch === 'error' ? 'stale' : null };
  if (status?.state === 'verified' && status.resolution_state === 'quarantined') return { ...view, problem: 'quarantined' };
  if (status?.state === 'verified') return { ...view, kind: 'done' as const, note: 'doneNote' };
  if (status && ['queued','verifying','retry_scheduled'].includes(status.state)) return { ...view, kind: 'checking' as const };
  if (status?.state === 'failed' && ['paused','failed','awaiting_verification'].includes(job.phase)) {
    return { ...view, primary: 'continue' as const, secondary: 'retry' as const, problem: 'uploadAgainFirst' };
  }
  if (status?.state === 'failed' && job.phase === 'completed') return { ...view, problem: 'cannotRestart' };
  if (status?.state === 'not_requested' && job.phase === 'awaiting_verification') return { ...view, kind: 'ready' as const, primary: 'verify' as const };
  if (status?.state === 'not_requested' && ['paused','failed'].includes(job.phase)) return { ...view,
    kind: job.phase === 'paused' ? 'paused' as const : 'attention' as const, primary: 'continue' as const, secondary: 'verify' as const, problem: job.reason ?? 'error' };
  return { ...view, problem: 'error' };
}
export class HelperError extends Error {
  constructor(readonly status: number, readonly code: string) { super(code); this.name = 'HelperError'; }
}
export function pairedHelper(token: string, actor: IntakeActor, request = fetch) {
  if (!/^[A-Za-z0-9_-]{43}$/.test(token)) throw new Error('pairing_required');
  const helper = async <T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> => {
    if (!/^\/v1\/(?:info|sessions|jobs(?:\/[a-f0-9-]{36}(?:\/(?:prepare|transfer|pause|receipt))?)?)$/.test(path)) throw new Error('invalid_helper_path');
    const response = await request('http://127.0.0.1:43187' + path, { method: body === undefined ? 'GET' : 'POST',
      credentials: 'omit', redirect: 'error', cache: 'no-store', signal: requestSignal(signal),
      headers: { Authorization: 'Bearer ' + token, ...(body === undefined ? {} : { 'Content-Type': 'application/json' }) },
      body: body === undefined ? undefined : JSON.stringify(body) });
    const result = await response.json();
    if (!response.ok) throw new HelperError(response.status, result?.error ?? 'helper_unavailable');
    return result as T;
  };
  return { request: helper, async connect() {
    const info = await helper<HelperInfo>('/v1/info');
    if (info.version !== 1 || info.centre_id !== actor.upload_centre_id || info.device_id !== actor.upload_device_id || info.recovery_required) {
      throw new Error('helper_identity_mismatch');
    }
    return info;
  }};
}
export type PairedHelper = ReturnType<typeof pairedHelper>;
const cloud = async <T>(path: string, body?: unknown): Promise<T> => {
  const result = await call<T>(path, { method: body === undefined ? 'GET' : 'POST', signal: AbortSignal.timeout(120000),
    ...(body === undefined ? {} : { body: JSON.stringify(body) }) });
  if (!result) throw new Error('empty_intake_response');
  return result;
};
export function statusPath(job: Pick<IntakeJob, 'record' | 'ingest_id' | 'target_batch_id'>) {
  if (!job.record || !job.ingest_id || !job.target_batch_id ||
    ![job.record.episode_id, job.ingest_id, job.target_batch_id].every(id => UUID.test(id))) throw new Error('registration_required');
  return `/upload-batches/${job.target_batch_id}/upload-status?episode_id=${job.record.episode_id}&ingest_id=${job.ingest_id}`;
}
export async function currentIntakeStatus(job: Pick<IntakeJob, 'record' | 'ingest_id' | 'target_batch_id'>, signal?: AbortSignal) {
  const result = await call<IntakeStatus>(statusPath(job), { signal: requestSignal(signal) });
  if (!result || result.episode_id !== job.record?.episode_id || result.ingest_id !== job.ingest_id || result.batch_id !== job.target_batch_id) throw new Error('status_identity_mismatch');
  return result;
}
/** Every retry asks the provider-backed plan for missing parts; helper state is never cloud proof. */
export async function sendPreparedJob(job: IntakeJob, helper: PairedHelper, request = cloud, live = () => !sessionIsEnding()) {
  if (!job.record || !job.files || ![job.id, job.record.episode_id, job.batch_id, job.handover_id].every(id => UUID.test(id))) throw new Error('measurement_required');
  let registration: IntakeRegistration;
  try { registration = await request<IntakeRegistration>(`/upload-batches/episode-registration?episode_id=${job.record.episode_id}`); }
  catch (error) {
    if (!(error instanceof ApiError) || error.status !== 404 || error.message !== 'episode_not_registered') throw error;
    if (!live()) return;
    await request('/upload-batches', { id: job.batch_id, handover_id: job.handover_id, import_started_at: new Date().toISOString() });
    if (!live()) return;
    const result = await request<{ episodes: (IntakeRegistration & { outcome?: string })[] }>(`/upload-batches/${job.batch_id}/episodes`, { episodes: [job.record] });
    const row = result.episodes?.[0];
    if (!row?.outcome || row.episode_id !== job.record.episode_id) throw new Error('registration_refused');
    registration = { ...row, batch_id: job.batch_id };
  }
  if (registration.episode_id !== job.record.episode_id || !UUID.test(registration.ingest_id) || !UUID.test(registration.batch_id) ||
    (job.ingest_id && job.ingest_id !== registration.ingest_id) || (job.target_batch_id && job.target_batch_id !== registration.batch_id)) throw new Error('registration_changed');
  const identity = { episode_id: registration.episode_id, ingest_id: registration.ingest_id };
  if (!live()) return;
  const plan = await request<{ files: unknown[] }>(`/upload-batches/${registration.batch_id}/upload-plan`, { ...identity, files: job.files });
  if (!live()) return;
  await helper.request(`/v1/jobs/${job.id}/transfer`, { ...identity, batch_id: registration.batch_id, plan: { ...identity, files: plan.files } });
  return registration;
}
export async function enqueueIntakeVerification(job: IntakeJob, recovery?: { retry?: boolean; retry_reason?: string; takeover_reason?: string }, live = () => !sessionIsEnding()) {
  await currentIntakeStatus(job); // Reconcile first, including after a lost completion response.
  if (!live()) return;
  return cloud<IntakeStatus>(`/upload-batches/${job.target_batch_id}/upload?source=direct&async=1`,
    { episode_id: job.record!.episode_id, ingest_id: job.ingest_id, ...recovery });
}
export async function reconnectTakenOverJob(job: IntakeJob, helper: PairedHelper, live = () => !sessionIsEnding()) {
  const actor = await intakeActor(), status = await currentIntakeStatus(job);
  if (!status.current_operator_owns_job || actor.upload_centre_id !== job.centre_id || actor.upload_device_id !== job.device_id) throw new Error('takeover_refused');
  if (!live()) return;
  return helper.request<IntakeJob>(`/v1/jobs/${job.id}/receipt`, { ...status, acting_operator_id: actor.operator_id,
    takeover_reason: 'Reconnect local association after confirmed server takeover' });
}
